export const MODE_ROUTE_COLOR_BY_TYPE = {
  driving: "#38bdf8",
  walking: "#34d399",
  random: "#603d1c",
};

export const DRIVING_ALTERNATE_ROUTE_KEYS = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
];

export function indexToAlphabetCode(index = 0) {
  let value = Number(index) + 1;
  if (!Number.isFinite(value) || value < 1) return "a";
  let label = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    label = String.fromCharCode(97 + remainder) + label;
    value = Math.floor((value - 1) / 26);
  }
  return label;
}

export function normalizeDrivingRouteGroupKey(
  value = "",
  routeGroupName = "",
  fallbackOrder = 0,
) {
  const normalizedValue = String(value || "")
    .trim()
    .toUpperCase();
  const detourMatch = normalizedValue.match(/^DETOUR([A-J])$/);
  if (detourMatch?.[1] && DRIVING_ALTERNATE_ROUTE_KEYS.includes(detourMatch[1])) {
    return detourMatch[1];
  }
  if (DRIVING_ALTERNATE_ROUTE_KEYS.includes(normalizedValue)) {
    return normalizedValue;
  }
  const normalizedRouteGroupName = String(routeGroupName || "")
    .trim()
    .toUpperCase();
  const match = normalizedRouteGroupName.match(/^(\d+)([A-J])$/);
  if (match?.[2] && DRIVING_ALTERNATE_ROUTE_KEYS.includes(match[2])) {
    return match[2];
  }
  if (
    normalizedValue === "MAIN" ||
    normalizedValue === String(Number(fallbackOrder) || "").trim()
  ) {
    return "main";
  }
  return "main";
}

export function buildDrivingRouteGroupName(order = 0, routeGroup = "main") {
  const pointOrder = Number(order) || 0;
  const normalizedOrder = pointOrder > 0 ? String(pointOrder) : "";
  const normalizedGroup = normalizeDrivingRouteGroupKey(routeGroup);
  if (!normalizedOrder)
    return normalizedGroup === "main" ? "" : normalizedGroup;
  return normalizedGroup === "main"
    ? normalizedOrder
    : `${normalizedOrder}${normalizedGroup}`;
}

export function buildPayloadRouteGroup(
  point = {},
  modeType = "",
  fallbackIndex = 0,
) {
  const normalizedMode = String(modeType || "")
    .trim()
    .toLowerCase();
  if (normalizedMode !== "driving") return "";
  const pointOrder =
    Number(point?.order ?? point?.number_point ?? fallbackIndex + 1) || 0;
  const routeGroup = normalizeDrivingRouteGroupKey(
    point?.routeGroup ?? point?.route_group ?? "",
    point?.routeGroupName ?? point?.route_group_name ?? "",
    pointOrder,
  );
  if (routeGroup === "main") return "main";
  return `detour${String(routeGroup || "")
    .trim()
    .toUpperCase()}`;
}

export function buildPayloadLabelCode(
  point = {},
  modeType = "",
  fallbackIndex = 0,
) {
  const normalizedMode = String(modeType || "")
    .trim()
    .toLowerCase();
  if (normalizedMode === "random") return "";
  if (normalizedMode === "walking") {
    return indexToAlphabetCode(fallbackIndex);
  }
  if (normalizedMode === "driving") {
    return buildDrivingLabelCode(point, fallbackIndex);
  }
  return "";
}

export function buildDrivingLabelCode(point = {}, fallbackIndex = 0) {
  const pointOrder =
    Number(point?.order ?? point?.number_point ?? fallbackIndex + 1) || 0;
  const explicitLabelCode = String(
    point?.labelCode ?? point?.label_code ?? "",
  ).trim();
  if (explicitLabelCode) return explicitLabelCode.toLowerCase();
  const routeGroupName = String(
    point?.routeGroupName ??
      point?.route_group_name ??
      buildDrivingRouteGroupName(
        pointOrder,
        point?.routeGroup ?? point?.route_group ?? "main",
      ),
  ).trim();
  if (!routeGroupName) return String(pointOrder || fallbackIndex + 1);
  const match = routeGroupName.match(/^(\d+)([A-Z]+)$/i);
  if (!match) return routeGroupName.toLowerCase();
  return `${match[1]}${String(match[2] || "").toLowerCase()}`;
}

export function isMainDrivingRouteGroup(
  routeGroup = "",
  routeGroupName = "",
  order = 0,
) {
  return (
    normalizeDrivingRouteGroupKey(routeGroup, routeGroupName, order) === "main"
  );
}

export function normalizeDrivingPointRouteGroup(point = {}, fallbackOrder = 0) {
  const pointOrder = Number(point?.order || fallbackOrder || 0) || 0;
  const routeGroup = normalizeDrivingRouteGroupKey(
    point?.routeGroup ?? point?.route_group ?? "",
    point?.routeGroupName ?? point?.route_group_name ?? "",
    pointOrder,
  );
  return {
    routeGroup,
    routeGroupName: buildDrivingRouteGroupName(pointOrder, routeGroup),
    labelCode: buildDrivingLabelCode(point, pointOrder - 1),
    routeGroupParentPointId: "",
  };
}

export function groupDrivingPointsByRoute(pointsList = []) {
  const groups = new Map();
  (Array.isArray(pointsList) ? pointsList : []).forEach((point) => {
    const pointOrder = Number(point?.number_point ?? point?.order ?? 0) || 0;
    const routeGroup = normalizeDrivingRouteGroupKey(
      point?.routeGroup ?? point?.route_group ?? "",
      point?.routeGroupName ?? point?.route_group_name ?? "",
      pointOrder,
    );
    if (!groups.has(routeGroup)) groups.set(routeGroup, []);
    groups.get(routeGroup).push(point);
  });
  return Array.from(groups.entries())
    .sort(([a], [b]) => {
      if (a === b) return 0;
      if (a === "main") return -1;
      if (b === "main") return 1;
      return a.localeCompare(b);
    })
    .map(([routeGroup, points]) => ({
      routeGroup,
      routeGroupName: routeGroup === "main" ? "Main" : routeGroup,
      points: [...points].sort(
        (a, b) =>
          Number(a?.number_point ?? a?.order ?? 0) -
          Number(b?.number_point ?? b?.order ?? 0),
      ),
    }));
}

export function resolveTourId(tour) {
  return tour?.id || tour?.tur_id || tour?.tourId || tour?.code || "";
}

export function normalizeModeType(modeType) {
  const raw = String(modeType || "")
    .toLowerCase()
    .trim();
  return raw || "custom";
}

export function extractRouteTypes(source) {
  if (!source) return [];
  if (
    source?.tour_types &&
    typeof source.tour_types === "object" &&
    !Array.isArray(source.tour_types)
  ) {
    return [source.tour_types];
  }
  if (Array.isArray(source?.tour_types)) return source.tour_types;
  if (
    source?.routeTypes &&
    typeof source.routeTypes === "object" &&
    !Array.isArray(source.routeTypes)
  ) {
    return [source.routeTypes];
  }
  if (Array.isArray(source?.routeTypes)) return source.routeTypes;
  if (
    source?.modes &&
    typeof source.modes === "object" &&
    !Array.isArray(source.modes)
  ) {
    return [source.modes];
  }
  if (Array.isArray(source?.modes)) return source.modes;
  return [];
}

export function buildModeOptionsFromRouteTypes({
  ownerId = "",
  routeTypes = [],
} = {}) {
  const seen = new Set();
  const options = [];
  (Array.isArray(routeTypes) ? routeTypes : []).forEach((routeType) => {
    const type = normalizeModeType(routeType?.type);
    if (seen.has(type)) return;
    seen.add(type);
    options.push({
      value: type,
      label: type.charAt(0).toUpperCase() + type.slice(1),
      routeTypeId: routeType?.id || "",
      ownerId: String(ownerId || ""),
    });
  });
  return options;
}

export function buildModeOptions(startPoints = []) {
  const seen = new Set();
  const options = [];
  startPoints.forEach((point) => {
    const modes = Array.isArray(point?.modes) ? point.modes : [];
    modes.forEach((mode) => {
      const type = normalizeModeType(mode?.type);
      if (seen.has(type)) return;
      seen.add(type);
      options.push({
        value: type,
        label: type.charAt(0).toUpperCase() + type.slice(1),
      });
    });
  });
  if (!options.length) {
    return [
      { value: "driving", label: "Driving" },
      { value: "walking", label: "Walking" },
      { value: "random", label: "Random" },
    ];
  }
  return options;
}

export function buildModeOptionsFromPoint(point) {
  const ownerId = point?.id || "";
  const routeTypes = extractRouteTypes(point);
  const options = buildModeOptionsFromRouteTypes({ ownerId, routeTypes });
  return options.length ? options : buildModeOptions([]);
}

export function buildModePointDraft(point = {}) {
  const parsedRadius = Number(point.radiusMeters ?? 12);
  const pointOrder = Number(point.order) || 1;
  const drivingRouteMeta = normalizeDrivingPointRouteGroup(point, pointOrder);
  return {
    id: point.id || "",
    order: pointOrder,
    latitude: Number(point.lat ?? point.latitude ?? 0),
    longitude: Number(point.lng ?? point.longitude ?? 0),
    name: point.name || "",
    description: point.description || "",
    more_info: point.more_info || point.moreInfo || "",
    notes: String(point?.notes ?? point?.note ?? ""),
    radiusMeters:
      Number.isFinite(parsedRadius) && parsedRadius > 0 ? parsedRadius : 12,
    turnCase: Number(point.turnCase ?? 0) || 0,
    fromDirection: point.fromDirection || "",
    toDirection: point.toDirection || "",
    pointType: String(point.pointType ?? point.point_type ?? ""),
    routeGroup: drivingRouteMeta.routeGroup,
    routeGroupName: drivingRouteMeta.routeGroupName,
    routeGroupParentPointId: drivingRouteMeta.routeGroupParentPointId,
    multimedia: Array.isArray(point.multimedia) ? point.multimedia : [],
  };
}
